import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarRange, Check, ChevronDown } from 'lucide-react';
import Popover from '../ui/Popover';
import { formatDate } from '../../utils/format';
import { PERIOD_PRESETS, periodRange, presetForRange, type PeriodPreset } from './workLogsFiltersModel';
import DateInput from '../ui/DateInput';

interface PeriodControlProps {
  from: string;
  to: string;
  onChange: (range: { from: string; to: string }) => void;
  /** Injected for tests / stories; defaults to now. */
  today?: Date;
}

/**
 * One period control for time-based lists (D8, DESIGN_SYSTEM.md §10.3 rule 5):
 * "תקופה: החודש ▾" opens presets and a custom range. Applies immediately.
 */
export default function PeriodControl({ from, to, onChange, today = new Date() }: PeriodControlProps) {
  const { t } = useTranslation();
  const baseId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const preset: PeriodPreset = presetForRange(from, to, today);

  const valueLabel = preset !== 'custom'
    ? t(`workLogs.period.${preset}`)
    : from && to ? `${formatDate(from)} – ${formatDate(to)}`
      : from ? t('workLogs.period.since', { date: formatDate(from) })
        : t('workLogs.period.until', { date: formatDate(to) });

  const choose = (p: Exclude<PeriodPreset, 'custom'>) => {
    onChange(periodRange(p, today));
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? `${baseId}-panel` : undefined}
        className="btn btn-secondary"
      >
        <CalendarRange className="w-4 h-4" aria-hidden="true" />
        {/* The prefix is visual context; on phones the icon carries it (the name keeps it). */}
        <span className="sr-only sm:not-sr-only sm:text-text-2">{t('workLogs.period.trigger')}</span>
        <span className="font-medium tabular-nums">{valueLabel}</span>
        <ChevronDown className={`w-4 h-4 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={triggerRef}
        align="start"
        id={`${baseId}-panel`}
        role="dialog"
        ariaLabel={t('workLogs.period.label')}
        focusFirst
        className="w-72 max-w-[calc(100vw-2rem)] p-1.5"
      >
        <ul className="flex flex-col gap-0.5">
          {PERIOD_PRESETS.map((p) => {
            const selected = p === preset;
            return (
              <li key={p}>
                <button
                  type="button"
                  onClick={() => choose(p)}
                  aria-pressed={selected}
                  className={`menu-row ${selected ? 'font-medium' : ''}`}
                >
                  <span className="min-w-0 flex-1 text-start">{t(`workLogs.period.${p}`)}</span>
                  {selected && <Check size={16} className="flex-shrink-0 text-accent-text" aria-hidden="true" />}
                </button>
              </li>
            );
          })}
        </ul>
        <fieldset className="mt-1.5 border-t border-border px-2 pb-1.5 pt-2.5">
          <legend className="sr-only">{t('workLogs.period.custom')}</legend>
          <p className="mb-2 text-xs font-medium text-text-2" aria-hidden="true">{t('workLogs.period.custom')}</p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label htmlFor={`${baseId}-from`} className="mb-1 block text-xs text-text-2">{t('workLogs.filterFromDate')}</label>
              <DateInput
                id={`${baseId}-from`}
                dir="ltr"
                className="input text-start text-sm"
                value={from}
                max={to || undefined}
                onChange={(e) => onChange({ from: e.target.value, to })}
              />
            </div>
            <div>
              <label htmlFor={`${baseId}-to`} className="mb-1 block text-xs text-text-2">{t('workLogs.filterToDate')}</label>
              <DateInput
                id={`${baseId}-to`}
                dir="ltr"
                className="input text-start text-sm"
                value={to}
                min={from || undefined}
                onChange={(e) => onChange({ from, to: e.target.value })}
              />
            </div>
          </div>
        </fieldset>
      </Popover>
    </>
  );
}
